<template>
  <div class="ms-map">
    <GmapMap ref="map" :center="mapCenter" :options="mapOptions" :zoom="zoom" class="ms-map__canvas" @click="mapClick">
      <GmapMarker v-if="origin" :draggable="true" :icon="originIcon" :position="origin" :z-index="2000"
                  title="Ausgangspunkt – zum Verschieben ziehen" @dragend="moved"/>
      <GmapCircle v-if="origin && !area" :center="origin" :options="circleOptions" :radius="radiusKm * 1000"/>
      <template v-if="area && area.points.length">
        <GmapPolygon v-if="area.closed" :options="polygonOptions" :paths="[areaPath]"/>
        <GmapPolyline v-else :options="lineOptions" :path="areaPath"/>
        <GmapMarker v-for="(point, index) in areaPath" :key="`p${index}`" :draggable="true" :icon="vertexIcon(index)" :position="point"
                    :title="vertexTitle(index)" :z-index="3000 + index" @click="vertexClick(index)" @dblclick="vertexDblClick(index)"
                    @dragend="(e) => vertexMoved(index, e)"/>
      </template>
      <GmapMarker v-for="(row, index) in markers" :key="row._id" :icon="iconFor(row)" :label="labelFor(index)" :position="row.position"
                  :title="row.name" :z-index="row._id === highlightId ? 1500 : 100 - index"
                  @click="$emit('select', row._id)" @mouseout="$emit('hover', null)" @mouseover="$emit('hover', row._id)"/>
    </GmapMap>
    <div v-if="!origin" class="ms-map__empty">
      <v-icon large>mdi-map-search-outline</v-icon>
      <span>Wähle links einen Ausgangspunkt. Danach erscheinen hier die Treffer.</span>
    </div>
    <div v-else class="ms-map__chips">
      <span class="ms-map__chip"><v-icon x-small>mdi-crosshairs-gps</v-icon>{{ originLabel }}</span>
      <span class="ms-map__chip">{{ areaText }}<template v-if="searched"> · {{ rows.length }} Treffer</template></span>
    </div>
    <div v-if="origin && searched && rows.length" class="ms-map__legend">
      <template v-if="kind === 'institution'">
        <span v-for="item in coverageLegend" :key="item.text"><i :style="{ background: item.color }" class="ms-map__dot"/>{{ item.text }}</span>
      </template>
      <template v-else>
        <span><i :style="{ background: carColor }" class="ms-map__dot"/>Mit Auto</span>
        <span><i :style="{ background: noCarColor }" class="ms-map__dot"/>Ohne Auto</span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import * as VueGoogleMaps from 'vue2-google-maps';
import {Place} from '@/interfaces/place.interface';
import {LngLat} from '@/helpers/geoPolygon.helper';
import {addPoint, AreaDraft, closeDraft, movePoint, removePoint} from '@/components/acquisitionList/create/areaDraw.helper';
import {CAR_COLOR, COVERAGE, coordinatesOf, MapRow, MapSearchKind, markerColor, NO_CAR_COLOR} from './mapSearch.helper';

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_CLOUD_PLATFORM_API_KEY,
    libraries: 'places',
    region: 'De',
    language: 'de',
  },
});

const AREA_BLUE = '#1976D2';
/** Germany, until an origin is chosen */
const DEFAULT_CENTER = {lat: 51.1657, lng: 10.4515};
/** A double-click fires two clicks first; a click on the first point waits this long before it closes the area */
const DBLCLICK_MS = 260;

/** Map of the map search: draggable origin pin, radius and numbered result markers (numbers as in the list) */
export default Vue.extend({
  props: {
    originPlace: {type: Object as () => Place | null, default: null},
    originLabel: {type: String, default: ''},
    /** Rows in list order */
    rows: {type: Array as () => MapRow[], required: true},
    kind: {type: String as () => MapSearchKind, required: true},
    radiusKm: {type: Number, required: true},
    highlightId: {type: String, default: null},
    searched: {type: Boolean, default: false},
    /** Drawn area ("Bereich zeichnen"); null = radius. Points can be set, moved and deleted (emits update:area) */
    area: {type: Object as () => AreaDraft | null, default: null},
  },
  data: () => ({
    circleOptions: {fillColor: AREA_BLUE, fillOpacity: 0.07, strokeColor: AREA_BLUE, strokeOpacity: 0.7, strokeWeight: 1.5, clickable: false},
    polygonOptions: {fillColor: AREA_BLUE, fillOpacity: 0.1, strokeColor: AREA_BLUE, strokeOpacity: 0.95, strokeWeight: 2.5, clickable: false},
    lineOptions: {strokeColor: AREA_BLUE, strokeOpacity: 0.95, strokeWeight: 2.5, clickable: false},
    clickTimer: undefined as number | undefined,
    carColor: CAR_COLOR,
    noCarColor: NO_CAR_COLOR,
  }),
  computed: {
    google: () => VueGoogleMaps.gmapApi(),
    mapOptions(): Record<string, unknown> {
      return {
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        disableDoubleClickZoom: !!this.area,
        draggableCursor: this.area && !this.area.closed ? 'crosshair' : undefined,
      };
    },
    areaPath(): Array<{ lat: number; lng: number }> {
      return (this.area?.points || []).map((p) => ({lat: p[1], lng: p[0]}));
    },
    areaText(): string {
      if (!this.area) {
        return `Umkreis ${this.radiusText} km`;
      }
      return this.area.closed ? 'Bereich' : `Zeichenmodus · ${this.area.points.length} ${this.area.points.length === 1 ? 'Punkt' : 'Punkte'}`;
    },
    origin(): { lat: number; lng: number } | null {
      return coordinatesOf(this.originPlace);
    },
    mapCenter(): { lat: number; lng: number } {
      return this.origin || DEFAULT_CENTER;
    },
    zoom(): number {
      if (!this.origin) {
        return 6;
      }
      if (this.radiusKm <= 6) {
        return 12;
      }
      if (this.radiusKm <= 12) {
        return 11;
      }
      return this.radiusKm <= 22 ? 10 : 9;
    },
    radiusText(): string {
      return this.radiusKm.toLocaleString('de-DE');
    },
    markers(): Array<MapRow & { position: { lat: number; lng: number } }> {
      return this.rows
          .map((row) => ({...row, position: coordinatesOf(row.address) as { lat: number; lng: number }}))
          .filter((row) => !!row.position);
    },
    originIcon(): any {
      return this.symbol('#1565C0', '#FFFFFF', 10, 3);
    },
    coverageLegend(): Array<{ text: string; color: string }> {
      return Object.values(COVERAGE);
    },
  },
  methods: {
    symbol(fill: string, stroke: string, scale: number, strokeWeight: number) {
      if (!this.google) {
        return undefined;
      }
      return {path: this.google.maps.SymbolPath.CIRCLE, fillColor: fill, fillOpacity: 1, strokeColor: stroke, strokeWeight, scale};
    },
    iconFor(row: MapRow) {
      return this.symbol(markerColor(row), '#FFFFFF', row._id === this.highlightId ? 15 : 11, 2.5);
    },
    labelFor(index: number) {
      return {text: String(index + 1), color: '#FFFFFF', fontSize: '11px', fontWeight: '700'};
    },
    emitArea(next: AreaDraft) {
      if (next !== this.area) {
        this.$emit('update:area', next);
      }
    },
    toLngLat(latLng: any): LngLat | null {
      if (!latLng) {
        return null;
      }
      const round = (v: number) => Math.round(v * 1e6) / 1e6;
      return [round(latLng.lng()), round(latLng.lat())];
    },
    mapClick(e: any) {
      const point = this.toLngLat(e?.latLng);
      if (this.area && point) {
        this.emitArea(addPoint(this.area, point));
      }
    },
    closable(index: number): boolean {
      return index === 0 && !!this.area && !this.area.closed && this.area.points.length >= 3;
    },
    vertexIcon(index: number) {
      const closable = this.closable(index);
      return this.symbol(closable ? AREA_BLUE : '#FFFFFF', AREA_BLUE, closable ? 8 : 6, 2.5);
    },
    vertexTitle(index: number): string {
      return this.closable(index) ? 'Klicken schließt die Fläche · Doppelklick löscht den Punkt' : 'Ziehen verschiebt · Doppelklick löscht den Punkt';
    },
    vertexClick(index: number) {
      if (index !== 0 || !this.area || this.area.closed) {
        return;
      }
      window.clearTimeout(this.clickTimer);
      this.clickTimer = window.setTimeout(() => {
        if (this.area) {
          this.emitArea(closeDraft(this.area));
        }
      }, DBLCLICK_MS);
    },
    vertexDblClick(index: number) {
      window.clearTimeout(this.clickTimer);
      if (this.area) {
        this.emitArea(removePoint(this.area, index));
      }
    },
    vertexMoved(index: number, e: any) {
      const point = this.toLngLat(e?.latLng);
      if (this.area && point) {
        this.emitArea(movePoint(this.area, index, point));
      }
    },
    moved(e: any) {
      const lat = e?.latLng?.lat?.();
      const lng = e?.latLng?.lng?.();
      if (typeof lat === 'number' && typeof lng === 'number') {
        this.$emit('move', {lat, lng});
      }
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.clickTimer);
  },
});
</script>

<style scoped>
.ms-map {
  position: relative;
  height: 100%;
  min-height: 360px;
}

.ms-map__canvas {
  position: absolute;
  inset: 0;
}

.ms-map__empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  background: rgba(244, 245, 247, .82);
  color: rgba(15, 23, 42, .62);
  font-size: 14px;
}

.ms-map__empty .v-icon {
  color: rgba(15, 23, 42, .4) !important;
}

.ms-map__chips {
  position: absolute;
  left: 12px;
  top: 12px;
  right: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  pointer-events: none;
}

.ms-map__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: rgba(255, 255, 255, .95);
  color: rgba(15, 23, 42, .78);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .15);
}

.ms-map__chip .v-icon {
  color: inherit !important;
}

.ms-map__legend {
  position: absolute;
  left: 12px;
  bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .95);
  color: rgba(15, 23, 42, .7);
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .15);
}

.ms-map__legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.ms-map__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
